import React, { useState } from 'react';
import {
    Paper,
    Button,
    Typography,
    Stack,
    TextField,
    MenuItem,
    Select,
    InputAdornment,
    Alert,
    Snackbar,
    CircularProgress
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userService from 'api/userService';
import walletService from 'api/walletService';

export default function WalletFundManagement({ userId, userRole, currentWalletBalance }) {
    const queryClient = useQueryClient();

    const [amount, setAmount] = useState('');
    const [type, setType] = useState('credit');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const walletMutation = useMutation({
        mutationFn: (payload) => walletService.adminWalletFunding(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['adminCustomerDetails', userId]
            });

            setOpenSnackbar(true);
            setAmount('');
            setReason('');
            setError('');
        },
        onError: (err) => {
            setError(err?.response?.data?.message || 'Transaction failed.');
        }
    });

    const handleFund = () => {
        const numericAmount = parseFloat(amount);

        if (!numericAmount || numericAmount <= 0) {
            setError('Please enter a valid amount.');
            return;
        }

        if (!reason.trim()) {
            setError('An internal note/reason is required for auditing.');
            return;
        }

        setError('');

        walletMutation.mutate({
            owner: userId, // 👈 wallet owner
            role: userRole,
            amount: numericAmount,
            remark: reason.trim(), // 👈 maps to remark
            type: type === 'credit' ? 'CREDIT' : 'DEBIT'
        });
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 3, mt: 3, background: 'primaryLight' }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <AccountBalanceWalletIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                    Wallet Adjustment
                </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" mb={2}>
                Current Balance: <strong style={{ color: '#2e7d32' }}>₦{currentWalletBalance?.toLocaleString() || '0.00'}</strong>
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                    <Select size="small" value={type} onChange={(e) => setType(e.target.value)} sx={{ width: 150, bgcolor: 'white' }}>
                        <MenuItem value="credit">Credit (+)</MenuItem>
                        <MenuItem value="debit">Debit (-)</MenuItem>
                    </Select>

                    <TextField
                        label="Amount"
                        size="small"
                        type="number"
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        sx={{ bgcolor: 'white' }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>
                        }}
                    />
                </Stack>

                {/* 🎯 The Mandatory Reason Field */}
                <TextField
                    label="Internal Note / Reason"
                    placeholder="e.g. Refund for Order #7721 or Promotional Bonus"
                    size="small"
                    multiline
                    rows={2}
                    fullWidth
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{ bgcolor: 'white' }}
                    helperText="This note will be visible in the transaction history."
                />

                <Button
                    variant="contained"
                    color={type === 'credit' ? 'success' : 'error'}
                    fullWidth
                    sx={{ py: 1, fontWeight: 600 }}
                    onClick={handleFund}
                    disabled={!amount || !reason || walletMutation.isPending}
                >
                    {walletMutation.isPending ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        `Confirm ${type === 'credit' ? 'Deposit' : 'Withdrawal'}`
                    )}
                </Button>
            </Stack>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled">
                    Wallet updated successfully!
                </Alert>
            </Snackbar>
        </Paper>
    );
}
